import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '../stores/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  config => {
    const authStore = useAuthStore();
    if (authStore.accessToken) {
      config.headers.Authorization = `Bearer ${authStore.accessToken}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // If error is 401 and we haven't already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const authStore = useAuthStore();
        await authStore.refreshAccessToken();

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${authStore.accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        const authStore = useAuthStore();
        authStore.clearAuthState();
        window.location.href = '/admin/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  async login(credentials: { email: string; password: string }) {
    const response = await apiClient.post('/auth/login', {
      login: credentials.email,
      password: credentials.password,
    });
    return response.data;
  },

  async verifyTotpLogin(mfaToken: string, totpCode: string) {
    const response = await apiClient.post('/auth/platform/2fa/verify-login', {
      mfaToken,
      totpCode,
    });
    return response.data;
  },

  async setup2fa() {
    const response = await apiClient.post('/auth/platform/2fa/setup');
    return response.data as { otpauthUrl: string; qrCodeDataUrl: string; secret: string };
  },

  async enable2fa(totpCode: string) {
    const response = await apiClient.post('/auth/platform/2fa/enable', { totpCode });
    return response.data;
  },

  async disable2fa(totpCode: string) {
    const response = await apiClient.post('/auth/platform/2fa/disable', { totpCode });
    return response.data;
  },

  async logout() {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },

  async refreshToken(refreshToken: string) {
    const response = await apiClient.post('/auth/refresh', { refreshToken });
    return response.data;
  },
};

// Tenant API
export const tenantApi = {
  async create(data: any) {
    const response = await apiClient.post('/tenants', data);
    return response.data;
  },

  async findAll() {
    const response = await apiClient.get('/tenants');
    return response.data;
  },

  async findOne(id: string) {
    const response = await apiClient.get(`/tenants/${id}`);
    return response.data;
  },

  async update(id: string, data: any) {
    const response = await apiClient.put(`/tenants/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete(`/tenants/${id}`);
    return response.data;
  },

  async hardDelete(id: string) {
    const response = await apiClient.delete(`/tenants/${id}?hard=true`);
    return response.data;
  },

  // Discount management
  async createDiscount(tenantId: string, data: any) {
    const response = await apiClient.post(`/tenants/${tenantId}/discounts`, data);
    return response.data;
  },

  async getDiscountHistory(tenantId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/discounts`);
    return response.data;
  },

  async getActiveDiscount(tenantId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/discounts/active`);
    return response.data;
  },

  async updateDiscount(tenantId: string, discountId: string, data: any) {
    const response = await apiClient.put(`/tenants/${tenantId}/discounts/${discountId}`, data);
    return response.data;
  },

  async removeDiscount(tenantId: string, discountId: string) {
    const response = await apiClient.delete(`/tenants/${tenantId}/discounts/${discountId}`);
    return response.data;
  },

  // Admin user password setup email
  async getAdminUserInfo(tenantId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/admin-user`);
    return response.data;
  },

  async resendPasswordSetupEmail(tenantId: string) {
    const response = await apiClient.post(`/tenants/${tenantId}/resend-setup-email`);
    return response.data;
  },

  async getFailedEmails(tenantId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/failed-emails`);
    return response.data;
  },

  async validateSubdomain(subdomain: string) {
    const response = await apiClient.get(`/tenants/validate-subdomain/${subdomain}`);
    return response.data;
  },

  async checkSubdomainAvailability(subdomain: string) {
    const response = await apiClient.get(`/tenants/check-subdomain/${subdomain}`);
    return response.data;
  },

  async activate(id: string) {
    const response = await apiClient.post(`/tenants/${id}/activate`);
    return response.data;
  },

  async suspend(id: string) {
    const response = await apiClient.post(`/tenants/${id}/suspend`);
    return response.data;
  },

  async getTenantModules(tenantId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/modules`);
    return response.data;
  },

  async setTenantModules(tenantId: string, moduleIds: string[]) {
    const response = await apiClient.put(`/tenants/${tenantId}/modules`, { moduleIds });
    return response.data;
  },
};

// Platform User API
export const platformUserApi = {
  async create(data: any) {
    const response = await apiClient.post('/platform-users', data);
    return response.data;
  },

  async findAll() {
    const response = await apiClient.get('/platform-users');
    return response.data;
  },

  async findOne(id: string) {
    const response = await apiClient.get(`/platform-users/${id}`);
    return response.data;
  },

  async update(id: string, data: any) {
    const response = await apiClient.patch(`/platform-users/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete(`/platform-users/${id}`);
    return response.data;
  },

  async activate(id: string) {
    const response = await apiClient.post(`/platform-users/${id}/activate`);
    return response.data;
  },

  async deactivate(id: string) {
    const response = await apiClient.post(`/platform-users/${id}/deactivate`);
    return response.data;
  },

  async resetPassword(id: string, newPassword: string) {
    const response = await apiClient.post(`/platform-users/${id}/reset-password`, {
      newPassword,
    });
    return response.data;
  },
};

// Billing API
export const billingApi = {
  // Billing Plans
  async createPlan(data: any) {
    const response = await apiClient.post('/billing/plans', data);
    return response.data;
  },

  async getPlans(activeOnly = false) {
    const response = await apiClient.get(`/billing/plans?activeOnly=${activeOnly}`);
    return response.data;
  },

  async getPlan(id: string) {
    const response = await apiClient.get(`/billing/plans/${id}`);
    return response.data;
  },

  async updatePlan(id: string, data: any) {
    const response = await apiClient.patch(`/billing/plans/${id}`, data);
    return response.data;
  },

  async deletePlan(id: string) {
    const response = await apiClient.delete(`/billing/plans/${id}`);
    return response.data;
  },

  // Invoices
  async generateInvoice(data: any) {
    const response = await apiClient.post('/billing/invoices', data);
    return response.data;
  },

  async getAllInvoices() {
    const response = await apiClient.get('/billing/invoices');
    return response.data;
  },

  async getInvoicesByTenant(tenantId: string) {
    const response = await apiClient.get(`/billing/invoices/tenant/${tenantId}`);
    return response.data;
  },

  async getOverdueInvoices() {
    const response = await apiClient.get('/billing/invoices/overdue');
    return response.data;
  },

  async getInvoice(id: string) {
    const response = await apiClient.get(`/billing/invoices/${id}`);
    return response.data;
  },

  async markInvoiceAsPaid(id: string) {
    const response = await apiClient.post(`/billing/invoices/${id}/mark-paid`);
    return response.data;
  },

  async recreateInvoice(id: string) {
    const response = await apiClient.post(`/billing/invoices/${id}/recreate`);
    return response.data;
  },

  async waiveInvoice(id: string) {
    const response = await apiClient.post(`/billing/invoices/${id}/waive`);
    return response.data;
  },

  // Payments
  async recordPayment(data: any) {
    const response = await apiClient.post('/billing/payments', data);
    return response.data;
  },

  async getPaymentsByTenant(tenantId: string) {
    const response = await apiClient.get(`/billing/payments/tenant/${tenantId}`);
    return response.data;
  },

  async getPaymentsByInvoice(invoiceId: string) {
    const response = await apiClient.get(`/billing/payments/invoice/${invoiceId}`);
    return response.data;
  },
};

// Platform Settings API
export const platformSettingsApi = {
  async getSettings() {
    const response = await apiClient.get('/platform-settings');
    return response.data;
  },
  async updateSettings(data: {
    platformName?: string;
    platformDescription?: string;
    supportEmail?: string;
    supportPhone?: string;
    domain?: string;
    settings?: Record<string, unknown>;
  }) {
    const response = await apiClient.put('/platform-settings', data);
    return response.data;
  },
  async getMailServiceApiUrl() {
    const response = await apiClient.get('/platform-settings/email/api-url');
    return response.data;
  },
  async updateMailServiceApiUrl(apiUrl: string) {
    const response = await apiClient.put('/platform-settings/email/api-url', { apiUrl });
    return response.data;
  },
  async uploadLogo(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/platform-settings/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  async uploadFavicon(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/platform-settings/favicon', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  async deleteLogo() {
    const response = await apiClient.delete('/platform-settings/logo');
    return response.data;
  },
  async deleteFavicon() {
    const response = await apiClient.delete('/platform-settings/favicon');
    return response.data;
  },
};

// Email API
export const emailTemplateApi = {
  async getAllTemplates() {
    const response = await apiClient.get('/email-templates');
    return response.data;
  },

  async getTemplateById(id: string) {
    const response = await apiClient.get(`/email-templates/${id}`);
    return response.data;
  },

  async getTemplateByCode(code: string) {
    const response = await apiClient.get(`/email-templates/code/${code}`);
    return response.data;
  },

  async updateTemplate(id: string, data: { subject?: string; body?: string; name?: string }) {
    const response = await apiClient.put(`/email-templates/${id}`, data);
    return response.data;
  },

  async previewTemplate(id: string, variables?: Record<string, string>) {
    const response = await apiClient.post(`/email-templates/${id}/preview`, { variables });
    return response.data;
  },

  async sendTestEmail(id: string, to: string, variables?: Record<string, string>) {
    const response = await apiClient.post(`/email-templates/${id}/test`, { to, variables });
    return response.data;
  },
};

export const emailApi = {
  // Platform email account endpoints
  async createPlatformEmailAccount(data: {
    username: string;
    password: string;
    firstName: string;
    lastName: string;
  }) {
    const response = await apiClient.post('/email/platform/account', data);
    return response.data;
  },

  async connectPlatformEmailAccount(data: {
    username: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) {
    const response = await apiClient.post('/email/platform/account/connect', data);
    return response.data;
  },
  async getPlatformEmailAccount() {
    const response = await apiClient.get('/email/platform/account');
    return response.data;
  },
  async refreshPlatformToken(data?: { username?: string; password?: string }) {
    const response = await apiClient.post('/email/platform/account/refresh-token', data || {});
    return response.data;
  },
  // Platform SMTP config endpoints
  async createPlatformSmtpConfig(data: {
    host: string;
    port: number;
    username: string;
    password: string;
    useTls: boolean;
    fromEmail?: string;
    fromName?: string;
    replyTo?: string;
    isDefault?: boolean;
    lemConfigId?: string; // Optional: link to existing Lemu config when importing
  }) {
    const response = await apiClient.post('/email/platform/smtp-config', data);
    return response.data;
  },
  async getPlatformSmtpConfigs() {
    const response = await apiClient.get('/email/platform/smtp-configs');
    return response.data;
  },
  async syncPlatformSmtpConfigsFromLemu() {
    const response = await apiClient.get('/email/platform/smtp-configs/sync');
    return response.data;
  },
  async setDefaultPlatformSmtpConfig(configId: string) {
    const response = await apiClient.patch(`/email/platform/smtp-config/${configId}/set-default`);
    return response.data;
  },
  async updatePlatformSmtpConfig(
    configId: string,
    data: {
      host?: string;
      port?: number;
      username?: string;
      password?: string;
      useTls?: boolean;
      fromEmail?: string;
      fromName?: string;
      replyTo?: string;
      isDefault?: boolean;
    }
  ) {
    const response = await apiClient.put(`/email/platform/smtp-config/${configId}`, data);
    return response.data;
  },
  async deletePlatformSmtpConfig(configId: string) {
    const response = await apiClient.delete(`/email/platform/smtp-config/${configId}`);
    return response.data;
  },
  async testPlatformSmtpConfig(configId: string, dto: { to: string }) {
    const response = await apiClient.post(`/email/platform/smtp-config/${configId}/test`, dto);
    return response.data;
  },
  // Tenant email account endpoints
  async createTenantEmailAccount(
    tenantId: string,
    data: {
      username: string;
      password: string;
      firstName: string;
      lastName: string;
    }
  ) {
    const response = await apiClient.post(`/email/tenants/${tenantId}/account`, data);
    return response.data;
  },
  async getTenantEmailAccount(tenantId: string) {
    const response = await apiClient.get(`/email/tenants/${tenantId}/account`);
    return response.data;
  },
  async refreshTenantToken(tenantId: string, data?: { username?: string; password?: string }) {
    const response = await apiClient.post(
      `/email/tenants/${tenantId}/account/refresh-token`,
      data || {}
    );
    return response.data;
  },
  // Tenant SMTP config endpoints
  async createTenantSmtpConfig(
    tenantId: string,
    data: {
      host: string;
      port: number;
      username: string;
      password: string;
      useTls: boolean;
      fromEmail?: string;
      fromName?: string;
      replyTo?: string;
      isDefault?: boolean;
    }
  ) {
    const response = await apiClient.post(`/email/tenants/${tenantId}/smtp-config`, data);
    return response.data;
  },
  async getTenantSmtpConfigs(tenantId: string) {
    const response = await apiClient.get(`/email/tenants/${tenantId}/smtp-configs`);
    return response.data;
  },
  async getTenantSmtpConfig(tenantId: string, configId: string) {
    const response = await apiClient.get(`/email/tenants/${tenantId}/smtp-config/${configId}`);
    return response.data;
  },
  async updateTenantSmtpConfig(
    tenantId: string,
    configId: string,
    data: {
      host?: string;
      port?: number;
      secure?: boolean;
      username?: string;
      password?: string;
      from?: string;
      fromName?: string;
      replyTo?: string;
      enabled?: boolean;
    }
  ) {
    const response = await apiClient.put(
      `/email/tenants/${tenantId}/smtp-config/${configId}`,
      data
    );
    return response.data;
  },
  async deleteTenantSmtpConfig(tenantId: string, configId: string) {
    const response = await apiClient.delete(`/email/tenants/${tenantId}/smtp-config/${configId}`);
    return response.data;
  },
  async setDefaultTenantSmtpConfig(tenantId: string, configId: string) {
    const response = await apiClient.patch(
      `/email/tenants/${tenantId}/smtp-config/${configId}/set-default`
    );
    return response.data;
  },
  async testTenantSmtpConfig(tenantId: string, configId: string, to: string) {
    const response = await apiClient.post(
      `/email/tenants/${tenantId}/smtp-config/${configId}/test`,
      { to }
    );
    return response.data;
  },
  // Email sending
  async sendEmail(data: {
    to: string | string[];
    cc?: string | string[];
    bcc?: string | string[];
    subject: string;
    html?: string;
    text?: string;
    attachments?: Array<{ filename: string; content: string; contentType?: string }>;
    tenantId?: string;
  }) {
    const response = await apiClient.post('/email/send', data);
    return response.data;
  },
  async getEmailStatus(requestId: string, tenantId?: string) {
    const params = tenantId ? { tenantId } : {};
    const response = await apiClient.get(`/email/status/${requestId}`, { params });
    return response.data;
  },
  // Email logs
  async getEmailLogs(filters?: { status?: string; limit?: number; offset?: number }) {
    const params: any = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.limit) params.limit = filters.limit;
    if (filters?.offset) params.offset = filters.offset;
    const response = await apiClient.get('/email/logs', { params });
    return response.data;
  },
  async getTenantEmailLogs(
    tenantId: string,
    filters?: { status?: string; limit?: number; offset?: number }
  ) {
    const params: any = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.limit) params.limit = filters.limit;
    if (filters?.offset) params.offset = filters.offset;
    const response = await apiClient.get(`/email/logs/tenant/${tenantId}`, { params });
    return response.data;
  },
};

// Admin API - Modules (features)
export const adminApi = {
  async getModules() {
    const response = await apiClient.get('/admin/modules');
    return response.data;
  },

  async getModule(id: string) {
    const response = await apiClient.get(`/admin/modules/${id}`);
    return response.data;
  },

  async createModule(data: {
    code: string;
    name: string;
    description?: string;
    routePath: string;
    monthlyPrice?: number;
    isActive?: boolean;
  }) {
    const response = await apiClient.post('/admin/modules', data);
    return response.data;
  },

  async updateModule(
    id: string,
    data: { name?: string; description?: string; monthlyPrice?: number; isActive?: boolean }
  ) {
    const response = await apiClient.put(`/admin/modules/${id}`, data);
    return response.data;
  },

  async deleteModule(id: string) {
    const response = await apiClient.delete(`/admin/modules/${id}`);
    return response.data;
  },

  async activateModule(id: string) {
    const response = await apiClient.put(`/admin/modules/${id}/activate`);
    return response.data;
  },

  async deactivateModule(id: string) {
    const response = await apiClient.put(`/admin/modules/${id}/deactivate`);
    return response.data;
  },

  async getModuleAssignments(moduleId: string) {
    const response = await apiClient.get(`/admin/modules/${moduleId}/assignments`);
    return response.data;
  },

  async assignModuleToTenants(moduleId: string, estateIds: string[]) {
    const response = await apiClient.post(`/admin/modules/${moduleId}/tenants`, { estateIds });
    return response.data;
  },

  async assignToPricingTiers(id: string, pricingTierIds: string[]) {
    const response = await apiClient.post(`/admin/modules/${id}/pricing-tiers`, { pricingTierIds });
    return response.data;
  },

  async getModulesForPricingTier(pricingTierId: string) {
    const response = await apiClient.get(`/admin/modules/pricing-tier/${pricingTierId}/modules`);
    return response.data;
  },

  async getPermissions() {
    const response = await apiClient.get('/admin/permissions');
    return response.data;
  },

  async createPermission(data: any) {
    const response = await apiClient.post('/admin/permissions', data);
    return response.data;
  },

  async updatePermission(id: string, data: any) {
    const response = await apiClient.put(`/admin/permissions/${id}`, data);
    return response.data;
  },

  async deletePermission(id: string) {
    const response = await apiClient.delete(`/admin/permissions/${id}`);
    return response.data;
  },

  async activatePermission(id: string) {
    const response = await apiClient.put(`/admin/permissions/${id}/activate`);
    return response.data;
  },

  async deactivatePermission(id: string) {
    const response = await apiClient.put(`/admin/permissions/${id}/deactivate`);
    return response.data;
  },
};

// Demo Requests API
export const demoRequestApi = {
  async findAll(params?: { status?: string; page?: number; limit?: number }) {
    const response = await apiClient.get('/contact/demo-requests', { params });
    return response.data;
  },

  async updateStatus(id: string, status: string) {
    const response = await apiClient.patch(`/contact/demo-requests/${id}/status`, { status });
    return response.data;
  },
};

// Testimonials API
export const testimonialsApi = {
  async findAll() {
    const response = await apiClient.get('/testimonials?activeOnly=false');
    return response.data;
  },

  async create(data: {
    quote: string;
    name: string;
    role: string;
    initials?: string;
    color?: string;
    rating?: number;
    isActive?: boolean;
    sortOrder?: number;
  }) {
    const response = await apiClient.post('/testimonials', data);
    return response.data;
  },

  async update(
    id: string,
    data: {
      quote?: string;
      name?: string;
      role?: string;
      initials?: string;
      color?: string;
      rating?: number;
      isActive?: boolean;
      sortOrder?: number;
    }
  ) {
    const response = await apiClient.patch(`/testimonials/${id}`, data);
    return response.data;
  },

  async remove(id: string) {
    await apiClient.delete(`/testimonials/${id}`);
  },
};

// Export the apiClient for other API calls
export default apiClient;
